/**
 * Minimum order quantity (MOQ) and minimum order value (MOV).
 *
 * Keeps every checkout entry point (cart drawer, cart page, accelerated
 * buttons) disabled until the cart reaches the configured minimums, and keeps
 * the progress bars in the drawer, the cart page and the modal in sync.
 *
 * Config comes from snippets/minimum-order-modal.liquid as `window.minimumOrder`.
 */
(function () {
  const config = window.minimumOrder;
  if (!config || !config.enabled) return;

  const CHECKOUT_SELECTOR =
    '#CartDrawer-Checkout, #checkout, .cart__checkout-button, [name="checkout"], ' +
    '.cart__dynamic-checkout-buttons button, .cart__dynamic-checkout-buttons [role="button"]';

  const state = { total: 0, items: 0, met: false };

  // Order minimums. Both are floors on checkout: MOQ counts cart items, MOV is
  // the cart total in cents. Either at 0 switches that rule off; with both set
  // checkout only opens when both are met.
  const minQuantity = Math.max(0, parseInt(config.minQuantity, 10) || 0);
  const minValue = Math.round(Math.max(0, Number(config.minValue) || 0) * 100);
  const unitPlural = (config.quantityUnit || 'items').trim() || 'items';
  const unitSingular = unitPlural.replace(/s$/i, '') || unitPlural;

  function unit(count) {
    return count === 1 ? unitSingular : unitPlural;
  }

  function hasMinimums() {
    return minQuantity > 0 || minValue > 0;
  }

  function formatMoney(cents) {
    const amount = cents / 100;
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, config.moneyFormat);
    }
    return `$${amount.toFixed(2)}`;
  }

  /** What the cart is still short of, as { quantity, value } in items and cents. */
  function gap() {
    return {
      quantity: Math.max(0, minQuantity - state.items),
      value: Math.max(0, minValue - state.total),
    };
  }

  /** "2 more items and $15.00 more" — quantity first, then value. */
  function gapText(missing) {
    const parts = [];
    if (missing.quantity > 0) parts.push(`${missing.quantity} more ${unit(missing.quantity)}`);
    if (missing.value > 0) parts.push(`${formatMoney(missing.value)} more`);
    return parts.join(' and ');
  }

  // How full the bar should be, 0–100. With both minimums set the least-complete
  // one wins, so it only reaches 100% when checkout is genuinely open.
  function progress() {
    if (!hasMinimums()) return state.items > 0 ? 100 : 0;
    const ratios = [];
    if (minQuantity > 0) ratios.push(state.items / minQuantity);
    if (minValue > 0) ratios.push(state.total / minValue);
    return Math.min(100, Math.max(0, Math.min.apply(null, ratios) * 100));
  }

  function checkoutButtons() {
    return Array.from(document.querySelectorAll(CHECKOUT_SELECTOR)).filter(
      (node) => !node.closest('[data-minimum-order-modal]')
    );
  }

  function render() {
    const percentage = progress();

    // "2/4 items · $20.00/$35.00 (Add 2 more items and $15.00 more)" — each rule
    // that is switched on gets its own counter.
    const counters = [];
    if (minQuantity > 0) counters.push(`${state.items}/${minQuantity} ${unit(minQuantity)}`);
    if (minValue > 0) counters.push(`${formatMoney(state.total)}/${formatMoney(minValue)}`);
    const head = counters.join(' · ');
    const label = !head ? '' : state.met ? `${head} (Completed)` : `${head} (Add ${gapText(gap())})`;

    // Only write when something actually changed — a MutationObserver calls
    // this back after every cart re-render and must not loop on itself.
    const width = `${percentage}%`;
    document.querySelectorAll('[data-minimum-order-fill]').forEach((node) => {
      if (node.style.width !== width) node.style.width = width;
    });
    document.querySelectorAll('[data-minimum-order-label]').forEach((node) => {
      if (node.textContent !== label) node.textContent = label;
    });

    checkoutButtons().forEach((button) => {
      const blocked = !state.met;
      if (button.classList.contains('minimum-order-blocked') !== blocked) {
        button.classList.toggle('minimum-order-blocked', blocked);
      }
      if (button.getAttribute('aria-disabled') !== String(blocked)) {
        button.setAttribute('aria-disabled', String(blocked));
      }
    });

    const modalCheckout = document.querySelector('[data-minimum-order-checkout-form]');
    if (modalCheckout && modalCheckout.hidden === state.met) modalCheckout.hidden = !state.met;
  }

  async function refresh() {
    try {
      const response = await fetch(`${window.routes ? window.routes.cart_url : '/cart'}.js`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();

      state.total = cart.total_price;
      state.items = cart.items.reduce((sum, item) => sum + item.quantity, 0);

      const missing = gap();
      state.met = missing.quantity === 0 && missing.value === 0;
    } catch (error) {
      // Never lock the customer out because the cart could not be read.
      state.met = true;
      console.error(error);
    }
    render();
  }

  function openModal() {
    const modal = document.querySelector('[data-minimum-order-modal]');
    if (!modal) return;
    document.querySelector('cart-drawer')?.close?.();
    modal.hidden = false;
    render();
    updateArrows();
  }

  function closeModal() {
    const modal = document.querySelector('[data-minimum-order-modal]');
    if (modal) modal.hidden = true;
  }

  // Capture phase so the block runs before the theme's own handlers and before
  // the cart form submits.
  document.addEventListener(
    'click',
    (event) => {
      if (state.met) return;
      const button = event.target.closest(CHECKOUT_SELECTOR);
      if (!button || button.closest('[data-minimum-order-modal]')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      openModal();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (state.met) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.hasAttribute('data-minimum-order-checkout-form')) return;
      if (!form.action || !form.action.includes('/cart')) return;
      if (!form.querySelector('[name="checkout"]')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      openModal();
    },
    true
  );

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-minimum-order-close]')) closeModal();
  });

  /** Desktop arrows for the horizontal upsell row. */
  function updateArrows() {
    const track = document.querySelector('[data-mov-cards]');
    const prev = document.querySelector('[data-mov-scroll="prev"]');
    const next = document.querySelector('[data-mov-scroll="next"]');
    if (!track || !prev || !next) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= maxScroll - 1;
    if (prev.disabled !== atStart) prev.disabled = atStart;
    if (next.disabled !== atEnd) next.disabled = atEnd;
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mov-scroll]');
    if (!button) return;
    const track = document.querySelector('[data-mov-cards]');
    if (!track) return;

    const card = track.querySelector('.minimum-order-modal__card');
    const step = card ? card.getBoundingClientRect().width + 10 : track.clientWidth * 0.8;
    track.scrollBy({ left: button.dataset.movScroll === 'prev' ? -step : step, behavior: 'smooth' });
  });

  document.addEventListener(
    'scroll',
    (event) => {
      if (event.target instanceof Element && event.target.hasAttribute('data-mov-cards')) updateArrows();
    },
    true
  );

  window.addEventListener('resize', updateArrows);

  /** Add an upsell product straight from the modal, then re-check the minimum. */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-minimum-order-add]');
    if (!button || button.disabled) return;

    event.preventDefault();
    const variantId = button.dataset.minimumOrderAdd;
    button.disabled = true;

    try {
      const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items: [{ id: Number(variantId), quantity: 1 }] };
      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        body.sections = cartEl.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (response.ok && !data.status) {
        if (cartEl && data.sections) {
          // Refresh the drawer markup without letting it slide open over the modal.
          const autoOpens = cartEl.classList.contains('open-after-adding');
          if (autoOpens) cartEl.classList.remove('open-after-adding');
          cartEl.renderContents(data);
          if (autoOpens) setTimeout(() => cartEl.classList.add('open-after-adding'), 0);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'minimum-order' });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      await refresh();
      button.disabled = false;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeModal();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
    subscribe(PUB_SUB_EVENTS.quantityUpdate, refresh);
  }

  // Not every path publishes cartUpdate — the product card's <add-to-cart>
  // posts to /cart/add and re-renders without an event. Watching the request
  // itself catches quick add, quick view, drawer quantity changes and apps.
  const CART_MUTATION = /\/cart\/(add|change|update|clear)/;
  const nativeFetch = window.fetch;
  window.fetch = function (...args) {
    const request = nativeFetch.apply(this, args);
    const input = args[0];
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (CART_MUTATION.test(url)) {
      request.then(() => setTimeout(refresh, 50)).catch(() => {});
    }
    return request;
  };
  document.addEventListener('cart:refresh', refresh);
  document.addEventListener('cart:change', refresh);

  // The drawer and the cart page replace their markup wholesale after every
  // cart update, so re-apply the progress bar and the blocked state.
  let renderTimer;
  new MutationObserver(() => {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(render, 100);
  }).observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
